import { z } from 'zod';
import { CurrencySchema } from './stores.js';
/**
 * Nahrazuje reakce v Messengeru:
 *   found              = lajk
 *   not_found          = nastvany smajlik
 *   found_other_price  = nasla, ale za jinou cenu, nez cekala
 * Polozky bez hlidane ceny pouzivaji jen found / not_found.
 */
export const ItemStatusSchema = z.enum([
    'pending',
    'found',
    'not_found',
    'found_other_price',
]);
/** Odkud se polozka na seznam dostala. Slouzi k mereni, co se realne pouziva. */
export const ItemSourceSchema = z.enum([
    'manual',
    'template',
    'suggestion',
    'photo',
    'leaflet',
]);
/**
 * Mechanika slevy. Kaufland jede skoro vyhradne na 'absolute' (jedna cena),
 * Biedronka temer nikdy — tam je to podmineno poctem kusu.
 */
export const OfferMechanismSchema = z.enum([
    'absolute', // proste cena
    'per_unit_when_buying_n', // "przy zakupie 2, kazda z 2 sztuk 2,49"
    'nth_item_discount', // "drugi 70% taniej"
    'buy_x_get_y_free', // "1+1 gratis"
    'percent_off', // "40% taniej" bez uvedene vysledne ceny
]);
/**
 * Vysledek extrakce jedne nabidky z letaku nebo z fotky.
 * Stejne schema se pouziva jako JSON schema pro structured outputs Claude API,
 * jako validace odpovedi modelu a jako typ v review obrazovce.
 *
 * Proc tolik cenovych poli: v letaku Biedronky nese jedna nabidka az tri ceny
 * najednou — s kartou a pri splneni podminky, pred slevou, a bez karty nebo
 * nad denni limit. Jedno pole `price` by tise ulozilo tu nejnizsi a rozpocet
 * by sedel jen nahodou. Nazvy produktu zustavaji v originalnim jazyce letaku,
 * viz sekce 2.5 navrhu.
 */
export const ExtractedOfferSchema = z.object({
    name: z.string(),
    brand: z.string().nullable(),
    quantity: z.string().nullable(),
    mechanism: OfferMechanismSchema,
    /** Cena po sleve za kus, kdyz je podminka splnena. */
    promoPrice: z.number().nonnegative().nullable(),
    /** Bezna cena za kus — "cena przed obnizka" nebo cena bez vernostni karty. */
    regularPrice: z.number().nonnegative().nullable(),
    currency: CurrencySchema.nullable(),
    unitPrice: z.string().nullable(),
    /** Kolik kusu je potreba koupit, aby sleva platila. null = bez podminky. */
    requiredQty: z.number().int().positive().nullable(),
    /** Biedronka inzeruje vetsinu cen jen drzitelum karty Moja Biedronka. */
    loyaltyCardRequired: z.boolean(),
    /** "Limit dzienny 10 szt." — nad limit plati bezna cena. */
    dailyLimit: z.number().int().positive().nullable(),
    validFrom: z.iso.date().nullable(),
    validTo: z.iso.date().nullable(),
    confidence: z.number().min(0).max(1),
});
/**
 * Kolik sestra realne zaplati za `qty` kusu. Bez tohohle by soucet u
 * podminenych nabidek lhal — kdyz vezme jedno maslo misto dvou, plati
 * bezenou cenu, ne akcni.
 */
export function effectiveTotal(offer, qty) {
    const promo = offer.promoPrice;
    const regular = offer.regularPrice ?? promo;
    if (promo === null || regular === null)
        return null;
    const limited = offer.dailyLimit ?? Number.POSITIVE_INFINITY;
    const need = offer.requiredQty ?? 1;
    switch (offer.mechanism) {
        case 'absolute':
            return promo * Math.min(qty, limited) + regular * Math.max(0, qty - limited);
        case 'per_unit_when_buying_n': {
            if (qty < need)
                return regular * qty;
            const discounted = Math.min(qty, limited);
            return promo * discounted + regular * (qty - discounted);
        }
        case 'nth_item_discount':
        case 'buy_x_get_y_free': {
            // Ve dvojicich: prvni za beznou, druhy za akcni.
            const pairs = Math.floor(qty / 2);
            return pairs * (regular + promo) + (qty % 2) * regular;
        }
        case 'percent_off':
            return promo * qty;
    }
}
export const ExtractionResultSchema = z.object({
    items: z.array(ExtractedOfferSchema),
});
/** Pod touhle hranici review obrazovka polozku zvyrazni k rucni kontrole. */
export const LOW_CONFIDENCE_THRESHOLD = 0.7;
//# sourceMappingURL=items.js.map